import { Component } from '@theme/component';

/** localStorage flag — once set, the bar never shows again (no expiry, per spec). */
const STORAGE_KEY = 'sphatik-push-optin-dismissed';

/**
 * Push Notification Opt-in soft-prompt bar (site-wide).
 *
 * Slides down from the top `data-delay-ms` after load, unless the visitor
 * already chose "Enable" or "Not Now" on a previous visit (tracked in
 * localStorage — permanent, not time-limited). Either button slides it back
 * up and persists the choice. "Enable" also triggers the browser's native
 * Notification permission prompt — this only captures permission; actually
 * sending push messages needs a push app (e.g. PushOwl) or custom backend,
 * which is outside what a theme alone can do.
 *
 * @extends {Component}
 */
export class SphatikPushOptinComponent extends Component {
  /** @type {number | undefined} */
  #timeoutId;

  connectedCallback() {
    super.connectedCallback();

    if (this.#isDismissed()) {
      this.setAttribute('hidden', '');
      return;
    }

    const delay = Number(this.dataset.delayMs) || 2000;
    this.#timeoutId = window.setTimeout(() => this.classList.add('sphatik-push-optin--visible'), delay);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearTimeout(this.#timeoutId);
  }

  handleEnable() {
    if ('Notification' in window && typeof Notification.requestPermission === 'function') {
      Notification.requestPermission().catch(() => {
        // Ignore — dismissal below doesn't depend on the outcome.
      });
    }
    this.#dismiss();
  }

  handleDismiss() {
    this.#dismiss();
  }

  #dismiss() {
    this.#persist();
    this.classList.remove('sphatik-push-optin--visible');

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.setAttribute('hidden', '');
      return;
    }

    // Take it out of layout/tab order only after the slide-up transition finishes.
    this.addEventListener('transitionend', () => this.setAttribute('hidden', ''), { once: true });
  }

  /** @returns {boolean} */
  #isDismissed() {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      // Storage unavailable (private browsing, disabled) — never suppress rather than throw.
      return false;
    }
  }

  #persist() {
    try {
      localStorage.setItem(STORAGE_KEY, 'true');
    } catch {
      // Storage unavailable — the bar will just show again next visit.
    }
  }
}

if (!customElements.get('sphatik-push-optin-component')) {
  customElements.define('sphatik-push-optin-component', SphatikPushOptinComponent);
}
